/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { XdaStringsService } from '../../services/xda-strings.service';
import { OptionListOptions } from '../../services/workflow.service';

@Component({
  selector: 'xrx-pagination',
  standalone: false,

  templateUrl: './xrx-pagination.component.html',
})
export class XrxPaginationComponent implements OnInit, OnChanges {
  private _page = 1;
  @Input() get page(): number {
    return this._page;
  }
  public set page(value: number) {
    this._page = value;
    this.pageSelect = this.pageList.filter((x) => x.page == this._page)[0];
  }
  private _pageSizeSelect: pageSizeType = {
    size: 10,
    key: '',
    value: '',
  };

  @Input() get pageSizeSelect(): pageSizeType {
    return this._pageSizeSelect;
  }

  public set pageSizeSelect(value: number) {
    this._pageSizeSelect = {
      size: value,
      key: '',
      value: '',
    };
    setTimeout(() => {
      this._pageSizeSelect = this.pageSizeList.filter(
        (x) => x.size == this.pageSize,
      )[0];
    }, 0);
  }
  @Input() pageSize = 10;
  @Input() showPageSizeList = true;
  @Input() pageSizeList: pageSizeType[];
  @Input() collectionSize: number;
  @Input() initialPageSize: number;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  pageSelect: pageListType = { page: 1, key: '', value: '', pageLabel: '' };
  pageList: pageListType[] = [];
  collectionChanged = false;

  private xdaTranslatePipe: XdaTranslate;
  constructor(private xdaStringsService: XdaStringsService) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {
    // if there is an initial page size passed in update pageSize
    if (this.initialPageSize) {
      this.pageSize = this.initialPageSize;
    }
    // if pageSizeList isn't false update the page Size selected to match the new pageSize and send the change to the parent component
    if (this.showPageSizeList !== false) {
      const pageSizeList = this.pageSizeList as pageSizeType[];
      setTimeout(() => {
        this._pageSizeSelect = pageSizeList.filter(
          (x) => x.size == this.pageSize,
        )[0];
        this.pageSizeChange.emit(this.pageSize);
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // monitor if collectionSize changes to regenerate the page list
    if (
      changes['collectionSize'] &&
      changes['collectionSize'].firstChange === false
    ) {
      this.collectionChanged = true;
    }
  }

  // act on page select (select list)
  selectPage(page: unknown): void {
    if ((page as { page: number }).page !== undefined) {
      const pageObj = page as { page: number };
      this.page = pageObj.page;
      this.pageChange.emit(this.page);
    }
  }

  // act on pageChange event from the ngb-pagination component (buttons)
  ngbPaginationChange(page: number): void {
    this.pageSelect = this.pageList.filter((x) => x.page == page)[0];
    this.pageChange.emit(page);
  }

  // generate the page list with the correct labels
  generatePageList(pages: number[]) {
    // optimize how often the page list gets regenerated
    if (
      this.pageList.length !== pages.length ||
      this.collectionSize < this.pageSize ||
      this.collectionChanged
    ) {
      // reset
      this.pageList = [];
      this.collectionChanged = false;

      // loop through the list of pages and generate the correct labels
      pages.forEach((page) => {
        // first page
        if (page == 1) {
          if (this.collectionSize < this.pageSize) {
            this.pageList.push({
              page: page,
              pageLabel: `${page}-${this.collectionSize} ${this.xdaTranslatePipe.transform('SDE_OF')} ${this.collectionSize}`,
              key: '',
              value: '',
            });
          } else {
            this.pageList.push({
              page: page,
              pageLabel: `${page}-${page * this.pageSize} ${this.xdaTranslatePipe.transform('SDE_OF')} ${this.collectionSize}`,
              key: '',
              value: '',
            });
          }
          // last page
        } else if (page == pages.length) {
          // middle pages
          this.pageList.push({
            page: page,
            pageLabel: `${(page - 1) * this.pageSize + 1}-${this.collectionSize} ${this.xdaTranslatePipe.transform('SDE_OF')} ${this.collectionSize}`,
            key: '',
            value: '',
          });
        } else {
          this.pageList.push({
            page: page,
            pageLabel: `${(page - 1) * this.pageSize + 1}-${page * this.pageSize} ${this.xdaTranslatePipe.transform('SDE_OF')} ${this.collectionSize}`,
            key: '',
            value: '',
          });
        }
      });
      // set the selected page, so it displays in the UI
      this.pageSelect = this.pageList[this.page - 1];
    }

    return this.pageList;
  }

  // act on page size select (select list)
  selectPageSize(pageSize: unknown): void {
    this.page = 1;
    this.pageChange.emit(this.page);

    if ((pageSize as { size: number }).size !== undefined) {
      const pageSizeObj = pageSize as { size: number };
      this.pageSize = pageSizeObj.size;
      this.pageSizeChange.emit(this.pageSize);
    }
  }
}

// xrx-select's list is of type OptionListOptions[] so pageListType needs to extend OptionListOptions to avoid type errors in the HTML template
export interface pageListType extends OptionListOptions {
  page: number;
  pageLabel: string;
}

// xrx-select's list is of type OptionListOptions[] so pageSizeType needs to extend OptionListOptions to avoid type errors in the HTML template
export interface pageSizeType extends OptionListOptions {
  size: number;
}
